import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Socket } from "socket.io-client";

import { createAuthedSocket } from "@/utils/createAuthedSocket";

import { useAppDispatch } from "@/redux/hooks";
import { api } from "@/redux/api";

import { authSelector } from "@/pages/Public/redux/auth.slice";

enum SignalScanSocketAction {
	JOIN_COMPANY = "signal-scan-join-company",
	SIGNAL_SCAN_FINISHED = "signal-scan-finished",
	ERROR = "signal-scan-error",
}

export interface SignalScanFinishedUpdate {
	companyId: number;
	trackerId: number;
	trackerOrganizationId: number;
	signalLastCheckedAt: string;
}

export interface SignalScanSocketContextType {
	isConnected: boolean;
}

/**
 * How long we wait for further finished scans before refreshing the table. A bulk import of 200
 * companies finishes 200 scans over time; without this every one of them would trigger its own
 * full-table refetch.
 */
export const SIGNAL_SCAN_INVALIDATION_DEBOUNCE_MS = 1500;

export const SignalScanSocketContext = createContext<SignalScanSocketContextType>({
	isConnected: false,
});

/**
 * Builds the tag invalidations for a batch of finished first scans: the specific rows' signal
 * queries (so a found signal renders) plus one `TrackerOrganization` refetch (so
 * `signalLastCheckedAt` refreshes and the spinner clears). Deliberately narrow — one table
 * refetch per batch, never one per row.
 */
export const buildSignalScanInvalidations = (trackerOrganizationIds: number[]) => [
	...[...new Set(trackerOrganizationIds)].map((id) => ({
		type: "TrackerSignals" as const,
		id,
	})),
	"TrackerOrganization" as const,
];

/**
 * Clears the "Looking for signals" state of the Profile Tracker table in place: the API emits once
 * a tracker company's first signal scan has been recorded, and we refresh just enough for the cell
 * to re-render with the real result (PREC-3616).
 */
export const SignalScanSocketProvider = ({ children }: { children: React.ReactNode }) => {
	const socketRef = useRef<Socket | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const dispatch = useAppDispatch();
	const auth = useSelector(authSelector);
	const token = auth?.token;
	const tokenRef = useRef(token);

	tokenRef.current = token;
	const companyId = auth.user?.company?.id;

	const pendingIdsRef = useRef<number[]>([]);
	const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!token || !companyId) {
			return;
		}

		const flush = () => {
			flushTimerRef.current = null;

			const ids = pendingIdsRef.current;

			pendingIdsRef.current = [];

			if (ids.length === 0) {
				return;
			}

			dispatch(api.util.invalidateTags(buildSignalScanInvalidations(ids)));
		};

		const newSocket = createAuthedSocket(import.meta.env.VITE_API_URL, () => tokenRef.current);

		let hasLoggedError = false;

		newSocket.on("connect", () => {
			newSocket.emit(SignalScanSocketAction.JOIN_COMPANY, String(companyId));
			hasLoggedError = false;
			setIsConnected(true);
		});

		newSocket.on("connect_error", (err) => {
			if (!hasLoggedError) {
				console.warn("Signal scan socket connection error:", err);
				hasLoggedError = true;
			}

			setIsConnected(false);
		});

		newSocket.on("disconnect", () => {
			setIsConnected(false);
		});

		newSocket.on(SignalScanSocketAction.SIGNAL_SCAN_FINISHED, (data: SignalScanFinishedUpdate) => {
			if (!data?.trackerOrganizationId) {
				return;
			}

			pendingIdsRef.current.push(data.trackerOrganizationId);

			if (flushTimerRef.current) {
				clearTimeout(flushTimerRef.current);
			}

			flushTimerRef.current = setTimeout(flush, SIGNAL_SCAN_INVALIDATION_DEBOUNCE_MS);
		});

		newSocket.on(SignalScanSocketAction.ERROR, (errorMessage: string) => {
			console.error("Signal scan socket error:", errorMessage);
		});

		socketRef.current = newSocket;

		return () => {
			if (flushTimerRef.current) {
				clearTimeout(flushTimerRef.current);
				flushTimerRef.current = null;
			}

			pendingIdsRef.current = [];
			newSocket.disconnect();
			socketRef.current = null;
		};
	}, [token, companyId, dispatch]);

	return (
		<SignalScanSocketContext.Provider value={{ isConnected }}>
			{children}
		</SignalScanSocketContext.Provider>
	);
};

export const useSignalScanSocketContext = () => useContext(SignalScanSocketContext);
